import { motion } from 'framer-motion';
import { Compass, Gauge } from 'lucide-react';
import { StatusDot } from '@/components/common/status-badge';
import { VehicleThumb } from '@/components/vehicles/vehicle-thumb';
import { cn } from '@/lib/utils';
import { contactSummary, STATUS_LABEL } from '@/lib/vehicle-status';
import type { LiveVehicle } from '@/lib/vehicle-status';

const TONE: Record<string, string> = {
  moving: 'text-moving',
  idle: 'text-idle',
  offline: 'text-offline',
  alert: 'text-alert',
};

/**
 * A card rather than a table row: thumbnail, plate, secondary id line, and a
 * status dot pinned to the corner — the shape the reference uses for its
 * vehicle list.
 */
export function VehicleRow({
  entry,
  selected,
  onSelect,
}: {
  entry: LiveVehicle;
  selected: boolean;
  onSelect: () => void;
}) {
  const { vehicle, status } = entry;

  return (
    <motion.button
      type="button"
      layout="position"
      onClick={onSelect}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ layout: { duration: 0.26, ease: [0.2, 0.8, 0.2, 1] }, duration: 0.16 }}
      className={cn(
        'group relative w-full rounded-xl border bg-card px-3 py-2.5 text-left transition-all',
        selected
          ? 'border-primary/35 bg-primary-soft/40 shadow-card'
          : 'hover:border-border hover:shadow-card',
      )}
    >
      <div className="flex items-start gap-3">
        <VehicleThumb make={vehicle.make} model={vehicle.model} status={status} />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="truncate text-[13.5px] font-semibold tracking-tight">
                {vehicle.make} {vehicle.model}
              </p>
              <p className="tnum truncate text-[11.5px] text-muted-foreground">
                ID : {vehicle.plateNumber}
              </p>
            </div>
            <StatusDot status={status} size={8} className="mt-1" />
          </div>

          <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
            <span className="tnum inline-flex items-center gap-1">
              <Gauge className="size-3.5 opacity-60" />
              {Math.round(entry.speedKph)}
              <span className="opacity-70">km/h</span>
            </span>
            <span className="tnum inline-flex items-center gap-1">
              <Compass className="size-3.5 opacity-60" />
              {Math.round(entry.headingDeg)}°
            </span>
            <span className={cn('ml-auto text-[10.5px] font-semibold', TONE[status])}>
              {STATUS_LABEL[status]}
            </span>
          </div>

          <p className="tnum mt-0.5 text-[10.5px] text-muted-foreground">
            {contactSummary(entry, true)}
            {vehicle.group ? ` · ${vehicle.group}` : ''}
          </p>
        </div>
      </div>
    </motion.button>
  );
}
